/**
 * The review engine's words for people who aren't signed in (k10, c074, c133): the homeowner who taps Unsubscribe
 * or a review link, the review card, the share message, and the plain reason a pasted or imported row wasn't asked.
 * US English, house voice. The review ask emails themselves are in review-ask.ts and email.ts.
 *
 * Nothing here asks how the job went or steers anyone by how they feel: every customer gets the same ask and the
 * same link, straight to Google (docs/43 §4).
 */

export const REVIEW_PUBLIC = {
  unsubscribe: {
    title: 'Unsubscribe',
    heading: (business: string) => `Stop emails from ${business}`,
    body: (business: string) => `Tap the button and ${business} won't email you about reviews again.`,
    button: 'Unsubscribe',
    doneTitle: "You're unsubscribed",
    done: (business: string) => `You won't get more emails from ${business}.`,
    doneNote: "There's nothing else to do. You can close this page.",
    invalidTitle: "This link doesn't work",
    invalid: "We couldn't read that unsubscribe link. Reply to the email and ask them to stop, and they will.",
  },
  link: {
    notReadyTitle: "This link isn't ready yet",
    notReady: "This review link isn't set up yet. Try it again soon.",
    missingTitle: "We couldn't find that page",
    missing: "This review link doesn't go anywhere. Check the address and try again.",
  },
  /** What the Share button hands the owner's phone. The owner sends it themselves, however they like. */
  share: (business: string, url: string) => `Thanks for choosing ${business}. If you have a minute, a quick Google review really helps us. ${url}`,
  shareTitle: (business: string) => `Review ${business} on Google`,
  card: {
    /** The one line on the card. The same for every customer. */
    line: 'Scan to review us on Google',
    hint: 'Point your phone camera at the square',
    thanks: (business: string) => `Thank you from ${business}`,
  },
  /** Why this email reached them, under the business address in every review ask. */
  why: (business: string) => `You got this email because ${business} did a job for you.`,
  unsubscribeLink: 'Unsubscribe',
} as const;

/** Why a row in a paste or an import wasn't asked, in the owner's words. Keys are the contract's `AskVerdict`. */
export const ASK_VERDICT: Record<string, string | null> = {
  ok: null,
  bad_email: "That doesn't look like an email address.",
  duplicate: 'Already asked in the last 12 months.',
  opted_out: 'They asked not to be emailed.',
  undeliverable: "Email to this address doesn't get through.",
  throwaway: "That's a throwaway address. Ask for their usual email.",
  no_reply_address: 'Nobody reads that address. Ask for their own email.',
  own_team: "That's someone on your team. Reviews have to come from customers.",
  not_today: "That job wasn't today. An import is for today's jobs only.",
  no_date: "We couldn't read the job date on this row.",
  over_limit: "That's past today's limit. It can go tomorrow.",
};

/** Refusals the review ask routes answer with, shown as they are (the app's `ownWords`). */
export const ASK_REFUSAL = {
  needEmail: 'Review requests go by email. Enter their email address.',
  dailyLimit: (limit: number) => `You've hit today's limit of ${limit}. The rest can go tomorrow.`,
  paused: "Review requests are paused on your account for now. We'll tell you when they're back.",
  notReady: "We're still connecting your Google review link. You can ask as soon as it's ready.",
  pasteTooMany: (max: number) => `That's a lot for one go. Paste up to ${max} emails at a time.`,
  importTooMany: (max: number) => `That file has too many rows for one day. An import takes up to ${max}.`,
  nothingToSend: 'None of those can be asked. Check the notes beside each one.',
} as const;
